import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type KeyboardEvent } from 'react';
import { useIsTabletOrMobile } from '../hooks/useIsMobile';
import { useAppStore } from '../stores/appStore';
import { useFileEditorStore } from '../stores/fileEditorStore';
import { buildApiUrl } from '../utils/serverOrigin';
import MarkdownRenderer from './MarkdownRenderer';

// ── Types ────────────────────────────────────────────────────────────────

interface LaunchReadinessItem {
  name: string;
  status: 'done' | 'in-progress' | 'pending' | 'deferred';
}

interface LaunchReadiness {
  done: number;
  total: number;
  items: LaunchReadinessItem[];
}

interface ProjectTimeline {
  target: string | null;
  daysRemaining: number | null;
  currentWeek: number | null;
  totalWeeks: number | null;
  label: string;
  countdownLabel?: string;
}

interface Project {
  id: string;
  name: string;
  tagline: string;
  status: 'active' | 'paused' | 'completed';
  summary: string;
  phase: string;
  launchReadiness: LaunchReadiness | null;
  timeline: ProjectTimeline | null;
}

interface Task {
  id: string;
  name: string;
  details?: string;
  owner: string;
  ownerEmoji: string;
  size: string;
  status: 'done' | 'in-progress' | 'pending' | 'blocked';
  blockedBy: string | null;
}

interface TrackProgress {
  done: number;
  total: number;
  percent: number;
}

interface Track {
  name: string;
  emoji: string;
  tasks: Task[];
  progress: TrackProgress;
}

interface SprintSummary {
  total: number;
  done: number;
  inProgress: number;
  blocked: number;
  pending: number;
  percent: number;
}

interface Gate {
  description: string;
  met: boolean;
}

interface Blocker {
  task?: string;
  description: string;
  owner?: string;
  severity?: string;
  blockedBy?: string | null;
}

interface Sprint {
  number: number;
  name: string;
  dates: { start: string; end: string };
  daysRemaining: number;
  tracks: Track[];
  summary: SprintSummary;
  gates: Gate[];
  blockers: Blocker[];
}

interface OwnerEfficiency {
  name: string;
  emoji: string;
  assigned: number;
  done: number;
  percent: number;
}

interface SprintBurndown {
  tasksPerDay: number;
  daysElapsed: number;
  daysRemaining: number;
  onTrack: boolean;
  note: string;
}

interface Efficiency {
  byOwner: OwnerEfficiency[];
  sprintBurndown: SprintBurndown;
}

interface DashboardData {
  schema: string;
  generatedAt: string;
  projects: Project[];
  sprint: Sprint;
  efficiency: Efficiency;
  highlights: string[];
  nextUp: string[];
}

// ── Utilities ────────────────────────────────────────────────────────────

function relativeTime(ts: string): string {
  const ms = new Date(ts).getTime();
  if (isNaN(ms)) return '';
  const diff = Math.floor((Date.now() - ms) / 1000);
  if (diff < 10) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return 'yesterday';
  return `${Math.floor(diff / 86400)}d ago`;
}

function isStale(ts: string): boolean {
  const ms = new Date(ts).getTime();
  return isNaN(ms) ? false : Date.now() - ms > 86400_000;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ── Sub-components ───────────────────────────────────────────────────────

function ProgressBar({ percent, accent = false }: { percent: number; accent?: boolean }) {
  const clamped = Math.min(100, Math.max(0, percent));
  return (
    <div className="db-progress-track">
      <div
        className={`db-progress-fill${accent ? ' db-progress-fill--accent' : ''}`}
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
}

function StatusBadge({ status }: { status: Task['status'] }) {
  const labels: Record<Task['status'], string> = {
    done: '✓ done',
    'in-progress': '⟳ active',
    pending: '○ pending',
    blocked: '⚑ blocked',
  };
  return (
    <span className={`db-status-badge db-status-badge--${status}`}>
      {labels[status]}
    </span>
  );
}

// Task details popup
interface TaskDetailsPopupProps {
  task: Task;
  onClose: () => void;
}

function TaskDetailsPopup({ task, onClose }: TaskDetailsPopupProps) {
  useEffect(() => {
    const handleKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const hasDetails = task.details && task.details.trim().length > 0;

  return (
    <div
      className="db-task-popup-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="task-popup-title"
    >
      <div
        className="db-task-popup-container"
        onClick={(e) => e.stopPropagation()}
        role="document"
      >
        <div className="db-task-popup-header">
          <div className="db-task-popup-title-row">
            <span className="db-task-popup-id">{task.id}</span>
            <h2 id="task-popup-title" className="db-task-popup-title">{task.name}</h2>
          </div>
          <button
            type="button"
            className="db-task-popup-close"
            onClick={onClose}
            aria-label="Close task details"
          >
            ✕
          </button>
        </div>
        <div className="db-task-popup-meta">
          <span className="db-task-popup-owner">{task.ownerEmoji} {task.owner}</span>
          <span className="db-task-popup-size">{task.size}</span>
          <StatusBadge status={task.status} />
        </div>
        <div className="db-task-popup-body">
          {hasDetails ? (
            <MarkdownRenderer content={task.details!} />
          ) : (
            <div className="db-task-popup-empty">No details available</div>
          )}
        </div>
      </div>
    </div>
  );
}

// Animated expand/collapse section
function Collapsible({ expanded, children }: { expanded: boolean; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | 'auto'>(expanded ? 'auto' : 0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (expanded) {
      const h = el.scrollHeight;
      setHeight(h);
      const timer = setTimeout(() => setHeight('auto'), 260);
      return () => clearTimeout(timer);
    } else {
      setHeight(el.scrollHeight);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setHeight(0));
      });
    }
  }, [expanded]);

  return (
    <div
      className="db-collapsible"
      style={{
        height: height === 'auto' ? 'auto' : `${height}px`,
        overflow: 'hidden',
        transition: 'height 240ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <div ref={ref}>{children}</div>
    </div>
  );
}

// ── Section: Project Cards ───────────────────────────────────────────────

function ProjectCard({ project }: { project: Project }) {
  const { timeline, launchReadiness } = project;
  const hasTimeline = timeline !== null;

  const urgencyClass = useMemo(() => {
    if (!timeline?.daysRemaining) return '';
    if (timeline.daysRemaining <= 7) return 'db-project-card--urgent';
    if (timeline.daysRemaining <= 14) return 'db-project-card--warning';
    return '';
  }, [timeline]);

  return (
    <div className={`db-project-card ${urgencyClass}`}>
      <div className="db-project-card-top">
        <div className="db-project-card-header">
          <span className="db-project-name">{project.name}</span>
          <span className={`db-project-status db-project-status--${project.status}`}>
            {project.status}
          </span>
        </div>
        <div className="db-project-tagline">{project.tagline}</div>
        <div className="db-project-phase">{project.phase}</div>
      </div>

      {hasTimeline && timeline && (
        <div className="db-project-timeline">
          <div className="db-project-timeline-label">{timeline.label}</div>
          {timeline.daysRemaining !== null && (
            <div className="db-project-countdown">
              <span className="db-project-countdown-num">{timeline.daysRemaining}</span>
              <span className="db-project-countdown-unit">days to {timeline.countdownLabel || 'launch'}</span>
            </div>
          )}
          {timeline.currentWeek !== null && timeline.totalWeeks !== null && (
            <div className="db-project-weeks">
              <ProgressBar percent={(timeline.currentWeek / timeline.totalWeeks) * 100} />
              <span className="db-project-weeks-label">
                Wk {timeline.currentWeek}/{timeline.totalWeeks}
              </span>
            </div>
          )}
        </div>
      )}

      {launchReadiness && (
        <div className="db-project-readiness">
          <div className="db-project-readiness-header">
            <span className="db-project-readiness-label">Launch Readiness</span>
            <span className="db-project-readiness-count">
              {launchReadiness.done}/{launchReadiness.total}
            </span>
          </div>
          <ProgressBar percent={(launchReadiness.done / launchReadiness.total) * 100} accent />
          <div className="db-readiness-items">
            {launchReadiness.items.map((item) => (
              <div key={item.name} className={`db-readiness-item db-readiness-item--${item.status}`}>
                <span className="db-readiness-dot" />
                <span className="db-readiness-item-name">{item.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Section: Sprint Progress ─────────────────────────────────────────────

function SprintProgressSection({
  sprint,
  defaultExpanded,
}: {
  sprint: Sprint;
  defaultExpanded: boolean;
}) {
  const [expandedTracks, setExpandedTracks] = useState<Set<string>>(
    () => new Set(defaultExpanded ? sprint.tracks.map((t) => t.name) : []),
  );
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  const toggleTrack = useCallback((name: string) => {
    setExpandedTracks((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const handleTaskClick = useCallback((task: Task) => {
    setSelectedTask(task);
  }, []);

  const handleClosePopup = useCallback(() => {
    setSelectedTask(null);
  }, []);

  const trackAccent = (t: Track): string => {
    if (t.progress.percent === 100) return 'done';
    if (t.progress.percent === 0) return 'zero';
    return 'active';
  };

  return (
    <div className="db-section-card">
      <div className="db-section-header">
        <span className="db-section-label">// Sprint Progress</span>
        <span className="db-section-stat">
          {sprint.summary.done}/{sprint.summary.total} tasks
        </span>
      </div>

      <div className="db-overall-progress">
        <div className="db-overall-progress-row">
          <ProgressBar percent={sprint.summary.percent} accent />
          <span className="db-overall-pct">{sprint.summary.percent}%</span>
        </div>
        <div className="db-sprint-chips">
          {sprint.summary.inProgress > 0 && (
            <span className="db-chip db-chip--active">{sprint.summary.inProgress} active</span>
          )}
          {sprint.summary.blocked > 0 && (
            <span className="db-chip db-chip--blocked">{sprint.summary.blocked} blocked</span>
          )}
          {sprint.summary.pending > 0 && (
            <span className="db-chip db-chip--pending">{sprint.summary.pending} pending</span>
          )}
        </div>
      </div>

      <div className="db-tracks">
        {sprint.tracks.map((track) => {
          const isExpanded = expandedTracks.has(track.name);
          const accent = trackAccent(track);
          return (
            <div key={track.name} className={`db-track db-track--${accent}`}>
              <button
                type="button"
                className="db-track-header"
                onClick={() => toggleTrack(track.name)}
                aria-expanded={isExpanded}
              >
                <span className="db-track-emoji">{track.emoji}</span>
                <span className="db-track-name">{track.name}</span>
                <div className="db-track-bar-wrap">
                  <div
                    className="db-track-bar-fill"
                    style={{ width: `${track.progress.percent}%` }}
                  />
                </div>
                <span className="db-track-fraction">
                  {track.progress.done}/{track.progress.total}
                </span>
                <span className={`db-track-chevron${isExpanded ? ' is-open' : ''}`}>▾</span>
              </button>
              <Collapsible expanded={isExpanded}>
                <div className="db-task-list">
                  {track.tasks.map((task) => (
                    <button
                      key={task.id}
                      type="button"
                      className={`db-task-row db-task-row--${task.status} db-task-row--clickable`}
                      onClick={() => handleTaskClick(task)}
                      aria-label={`View details for ${task.name}`}
                    >
                      <span className="db-task-id">{task.id}</span>
                      <span className="db-task-name">{task.name}</span>
                      <span className="db-task-owner">{task.ownerEmoji}</span>
                      <StatusBadge status={task.status} />
                    </button>
                  ))}
                </div>
              </Collapsible>
            </div>
          );
        })}
      </div>

      {/* Task details popup */}
      {selectedTask && (
        <TaskDetailsPopup task={selectedTask} onClose={handleClosePopup} />
      )}
    </div>
  );
}

// ── Section: Efficiency ──────────────────────────────────────────────────

function EfficiencySection({ efficiency }: { efficiency: Efficiency }) {
  const { sprintBurndown } = efficiency;

  return (
    <div className="db-section-card">
      <div className="db-section-header">
        <span className="db-section-label">// Efficiency</span>
        <span className={`db-ontrack-badge${sprintBurndown.onTrack ? '' : ' db-ontrack-badge--off'}`}>
          {sprintBurndown.onTrack ? '● on track' : '● off track'}
        </span>
      </div>

      <div className="db-owners">
        {efficiency.byOwner.map((owner) => (
          <div key={owner.name} className="db-owner-row">
            <span className="db-owner-emoji">{owner.emoji}</span>
            <span className="db-owner-name">{owner.name}</span>
            <div className="db-owner-bar-wrap">
              <div
                className="db-owner-bar-fill"
                style={{ width: `${owner.percent}%` }}
              />
            </div>
            <span className="db-owner-pct">{owner.percent}%</span>
            <span className="db-owner-fraction">
              {owner.done}/{owner.assigned}
            </span>
          </div>
        ))}
      </div>

      <div className="db-burndown-note">
        <span className="db-burndown-rate">{sprintBurndown.tasksPerDay} tasks/day</span>
        <span className="db-burndown-sep">·</span>
        <span className="db-burndown-text">{sprintBurndown.note}</span>
      </div>
    </div>
  );
}

// ── Section: Blockers ────────────────────────────────────────────────────

function BlockersSection({ blockers }: { blockers: Blocker[] }) {
  if (blockers.length === 0) return null;

  // Blockers may have a `task` field (chain-style) or just `description` + `severity`
  const hasChainData = blockers.some((b) => b.task);

  if (!hasChainData) {
    // Simple list mode — no chain logic needed
    return (
      <div className="db-section-card db-section-card--blockers">
        <div className="db-section-header">
          <span className="db-section-label db-section-label--danger">// Blockers</span>
          <span className="db-blocker-count">{blockers.length} blocking</span>
        </div>
        <div className="db-blockers">
          {blockers.map((blocker, idx) => (
            <div key={idx} className="db-blocker-item">
              <div className="db-blocker-main">
                <span className="db-blocker-desc">{blocker.description}</span>
                {blocker.owner && (
                  <span className="db-blocker-owner">{blocker.owner}</span>
                )}
                {blocker.severity && (
                  <span className="db-blocker-severity">{blocker.severity}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Build a chain: find what each blocker blocks
  // We want to show: TASK_ID → blocks X → blocks Y
  const blockedByMap: Record<string, string> = {};
  for (const b of blockers) {
    if (b.blockedBy && b.task) blockedByMap[b.task] = b.blockedBy;
  }

  // Build chains starting from root blockers (those not blocked by others in list)
  const blockersInList = new Set(blockers.map((b) => b.task).filter(Boolean));
  const rootBlockers = blockers.filter(
    (b) => !b.blockedBy || !blockersInList.has(b.blockedBy),
  );

  const buildChain = (task: string, depth = 0): string[] => {
    const downstream = blockers.filter((b) => b.blockedBy === task);
    const chain: string[] = [task];
    for (const d of downstream) {
      if (d.task) chain.push(...buildChain(d.task, depth + 1));
    }
    return chain;
  };

  return (
    <div className="db-section-card db-section-card--blockers">
      <div className="db-section-header">
        <span className="db-section-label db-section-label--danger">// Blockers</span>
        <span className="db-blocker-count">{blockers.length} blocking</span>
      </div>
      <div className="db-blockers">
        {rootBlockers.map((blocker, idx) => {
          const chain = blocker.task ? buildChain(blocker.task) : [];
          return (
            <div key={blocker.task ?? idx} className="db-blocker-item">
              <div className="db-blocker-main">
                {blocker.task && <span className="db-blocker-id">{blocker.task}</span>}
                <span className="db-blocker-desc">{blocker.description}</span>
                {blocker.owner && <span className="db-blocker-owner">{blocker.owner}</span>}
              </div>
              {chain.length > 1 && (
                <div className="db-blocker-chain">
                  {chain.map((id, i) => (
                    <span key={id}>
                      {i > 0 && <span className="db-chain-arrow">→ blocks </span>}
                      <span className="db-chain-id">{id}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Section: Highlights + Up Next ───────────────────────────────────────

function HighlightsSection({ highlights, nextUp }: { highlights: string[]; nextUp: string[] }) {
  return (
    <div className="db-bottom-row">
      <div className="db-section-card">
        <div className="db-section-header">
          <span className="db-section-label db-section-label--success">// Highlights</span>
        </div>
        <ul className="db-bullet-list db-bullet-list--highlights">
          {highlights.map((h, i) => (
            <li key={i} className="db-bullet-item">{h}</li>
          ))}
        </ul>
      </div>
      <div className="db-section-card">
        <div className="db-section-header">
          <span className="db-section-label">// Up Next</span>
        </div>
        <ul className="db-bullet-list db-bullet-list--nexup">
          {nextUp.map((n, i) => (
            <li key={i} className="db-bullet-item">{n}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ── Section: Sprint Gates ────────────────────────────────────────────────

function GatesSection({ gates }: { gates: Gate[] }) {
  const metCount = gates.filter((g) => g.met).length;
  return (
    <div className="db-section-card">
      <div className="db-section-header">
        <span className="db-section-label">// Sprint Gates</span>
        <span className="db-gates-count">
          {metCount}/{gates.length} met
        </span>
      </div>
      <div className="db-gates-grid">
        {gates.map((gate, i) => (
          <div key={i} className={`db-gate-item${gate.met ? ' db-gate-item--met' : ''}`}>
            <span className="db-gate-icon">{gate.met ? '✅' : '⬜'}</span>
            <span className="db-gate-desc">{gate.description}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Section: Game Files Browser ──────────────────────────────────────────

interface GameFileEntry {
  name: string;
  type: 'file' | 'directory';
  size?: number;
}

// ── Image Lightbox ───────────────────────────────────────────────────────

interface ImageLightboxProps {
  src: string;
  filename: string;
  fileSize?: number;
  filePath?: string;
  onClose: () => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function ImageLightbox({ src, filename, fileSize, filePath, onClose }: ImageLightboxProps) {
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') onClose();
  };

  useEffect(() => {
    const handleKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    setDimensions({ width: img.naturalWidth, height: img.naturalHeight });
  };

  return (
    <div
      className="img-lightbox-backdrop"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-label={`Image viewer: ${filename}`}
      tabIndex={-1}
    >
      <div
        className="img-lightbox-container"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="presentation"
      >
        <div className="img-lightbox-header">
          <span className="img-lightbox-filename">{filename}</span>
          <button
            type="button"
            className="img-lightbox-close"
            onClick={onClose}
            aria-label="Close image viewer"
          >
            ✕
          </button>
        </div>
        <div className="img-lightbox-body">
          <img
            src={src}
            alt={filename}
            className="img-lightbox-img"
            onLoad={handleImageLoad}
          />
        </div>
        <div className="img-lightbox-meta">
          {dimensions && (
            <span className="img-lightbox-meta-item">
              <span className="img-lightbox-meta-label">Dimensions:</span> {dimensions.width} × {dimensions.height}
            </span>
          )}
          {fileSize !== undefined && (
            <span className="img-lightbox-meta-item">
              <span className="img-lightbox-meta-label">Size:</span> {formatFileSize(fileSize)}
            </span>
          )}
          {filePath && (
            <span className="img-lightbox-meta-item img-lightbox-meta-path">
              <span className="img-lightbox-meta-label">Path:</span> {filePath}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ProjectFilesSection() {
  const [currentPath, setCurrentPath] = useState('');
  const [files, setFiles] = useState<GameFileEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lightboxImage, setLightboxImage] = useState<{ src: string; filename: string; fileSize?: number; filePath?: string } | null>(null);
  const [selectedProject, setSelectedProject] = useState<'game' | 'teams'>('teams');

  const openProjectFile = useFileEditorStore((state) => state.openProjectFile);

  const switchProject = (project: 'game' | 'teams') => {
    setSelectedProject(project);
    setCurrentPath('');
  };

  // Load files for current path
  useEffect(() => {
    let cancelled = false;
    const loadFiles = async () => {
      setLoading(true);
      setError(null);
      try {
        const pathQuery = currentPath ? `?path=${encodeURIComponent(currentPath)}` : '';
        const res = await fetch(buildApiUrl(`/api/projects/${selectedProject}/files${pathQuery}`, useAppStore.getState().gatewayUrl), {
          headers: { 'Authorization': `Bearer ${useAppStore.getState().gatewayToken}` },
        });
        if (!res.ok) throw new Error('Failed to load files');
        const data = await res.json();
        const fileList: GameFileEntry[] = (data.files ?? []).map((f: { name: string; type?: string; size?: number }) => ({
          name: f.name,
          type: (f.type === 'directory' ? 'directory' : 'file') as 'file' | 'directory',
          size: f.size,
        }));
        if (!cancelled) setFiles(fileList);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load files');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadFiles();
    return () => { cancelled = true; };
  }, [currentPath, selectedProject]);

  const isTextFile = (name: string): boolean => {
    return name.endsWith('.md') || name.endsWith('.txt');
  };

  // Sort files: folders first, then text files (.md, .txt), then other files
  const sortedFiles = useMemo(() => {
    const folders = files.filter(f => f.type === 'directory');
    const textFiles = files.filter(f => f.type === 'file' && isTextFile(f.name));
    const otherFiles = files.filter(f => f.type === 'file' && !isTextFile(f.name));
    return [...folders, ...textFiles, ...otherFiles];
  }, [files]);

  const mdFiles = sortedFiles.filter(f => f.type === 'file' && isTextFile(f.name));
  const folders = sortedFiles.filter(f => f.type === 'directory');
  const otherFiles = sortedFiles.filter(f => f.type === 'file' && !isTextFile(f.name));

  const navigateToFolder = (folderName: string) => {
    setCurrentPath(currentPath ? `${currentPath}/${folderName}` : folderName);
  };

  const navigateUp = () => {
    const parts = currentPath.split('/');
    parts.pop();
    setCurrentPath(parts.join('/'));
  };

  const navigateToBreadcrumb = (index: number) => {
    if (index < 0) {
      setCurrentPath('');
      return;
    }
    const parts = currentPath.split('/').filter(Boolean);
    setCurrentPath(parts.slice(0, index + 1).join('/'));
  };

  const isImageFile = (fileName: string): boolean => {
    const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
    return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext);
  };

  const handleFileClick = (file: GameFileEntry) => {
    const fullPath = currentPath ? `${currentPath}/${file.name}` : file.name;
    if (file.name.endsWith('.md') || file.name.endsWith('.txt')) {
      void openProjectFile(selectedProject, fullPath);
      return;
    }
    if (isImageFile(file.name)) {
      const token = useAppStore.getState().gatewayToken;
      const url = buildApiUrl(`/api/projects/${selectedProject}/raw-file?path=${encodeURIComponent(fullPath)}`, useAppStore.getState().gatewayUrl);
      // Fetch with auth header then create object URL so <img> can render it
      fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
        .then(async (res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const fileSize = parseInt(res.headers.get('X-File-Size') || '0', 10) || undefined;
          const filePath = res.headers.get('X-File-Path') || undefined;
          const blob = await res.blob();
          return { blob, fileSize, filePath };
        })
        .then(({ blob, fileSize, filePath }) => {
          const objectUrl = URL.createObjectURL(blob);
          setLightboxImage({ src: objectUrl, filename: file.name, fileSize, filePath });
        })
        .catch((err) => {
          console.error('[GameFiles] Failed to load image:', err);
        });
    }
  };

  // File type icon helper
  const getFileIcon = (fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) return '🖼️';
    if (['gd', 'gdscript'].includes(ext)) return '📜';
    if (['tscn', 'tres'].includes(ext)) return '🎮';
    if (['json', 'cfg'].includes(ext)) return '⚙️';
    if (ext === 'md') return '📄';
    return '📄';
  };

  return (
    <div className="db-section-card db-game-files">
      <div className="db-section-header">
        <span className="db-section-label">// Project Files</span>
      </div>

      {/* Project switcher toggle */}
      <div className="db-project-switcher">
        <button
          type="button"
          className={selectedProject === 'game' ? 'active' : ''}
          onClick={() => switchProject('game')}
        >
          Game
        </button>
        <button
          type="button"
          className={selectedProject === 'teams' ? 'active' : ''}
          onClick={() => switchProject('teams')}
        >
          Teams
        </button>
      </div>

      {/* Breadcrumb navigation */}
      <div className="db-gf-breadcrumb">
        <button
          type="button"
          onClick={() => navigateToBreadcrumb(-1)}
          className={`db-gf-crumb${!currentPath ? ' db-gf-crumb--active' : ''}`}
        >
          {selectedProject}
        </button>
        {currentPath.split('/').filter(Boolean).map((segment, idx) => (
          <span key={idx} className="db-gf-crumb-sep">
            <span className="db-gf-crumb-arrow">/</span>
            <button
              type="button"
              onClick={() => navigateToBreadcrumb(idx)}
              className={`db-gf-crumb${idx === currentPath.split('/').filter(Boolean).length - 1 ? ' db-gf-crumb--active' : ''}`}
            >
              {segment}
            </button>
          </span>
        ))}
      </div>

      {loading ? (
        <div className="db-gf-loading">Loading files...</div>
      ) : error ? (
        <div className="db-gf-error">{error}</div>
      ) : (
        <div className="db-gf-browser">
          {/* Up navigation when in subfolder */}
          {currentPath && (
            <button type="button" className="db-gf-up-btn" onClick={navigateUp}>
              <span className="db-gf-up-icon">↑</span>
              <span>Up</span>
            </button>
          )}

          {/* Folders - compact grid */}
          {folders.length > 0 && (
            <div className="db-gf-folders-grid">
              {folders.map((folder) => (
                <button
                  key={folder.name}
                  type="button"
                  className="db-gf-folder-item"
                  onClick={() => navigateToFolder(folder.name)}
                >
                  <span className="db-gf-folder-icon">📁</span>
                  <span className="db-gf-folder-name">{folder.name}</span>
                </button>
              ))}
            </div>
          )}

          {/* Markdown files - vertical list */}
          {mdFiles.length > 0 && (
            <div className="db-gf-md-list">
              {mdFiles.map((file) => (
                <button
                  key={file.name}
                  type="button"
                  className="db-gf-md-item"
                  onClick={() => handleFileClick(file)}
                >
                  <span className="db-gf-md-icon">📄</span>
                  <span className="db-gf-md-name">{file.name}</span>
                </button>
              ))}
            </div>
          )}

          {/* Other files (including images) - compact grid */}
          {otherFiles.length > 0 && (
            <div className="db-gf-other-grid">
              {otherFiles.map((file) => {
                const clickable = isImageFile(file.name);
                return clickable ? (
                  <button
                    key={file.name}
                    type="button"
                    className="db-gf-other-item db-gf-other-item--clickable"
                    onClick={() => handleFileClick(file)}
                    title={`View ${file.name}`}
                  >
                    <span className="db-gf-other-icon">{getFileIcon(file.name)}</span>
                    <span className="db-gf-other-name">{file.name}</span>
                  </button>
                ) : (
                  <div key={file.name} className="db-gf-other-item">
                    <span className="db-gf-other-icon">{getFileIcon(file.name)}</span>
                    <span className="db-gf-other-name">{file.name}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Empty state */}
          {files.length === 0 && !loading && (
            <div className="db-gf-empty">No files in this folder</div>
          )}
        </div>
      )}

      {/* Image Lightbox */}
      {lightboxImage && (
        <ImageLightbox
          src={lightboxImage.src}
          filename={lightboxImage.filename}
          fileSize={lightboxImage.fileSize}
          filePath={lightboxImage.filePath}
          onClose={() => {
            // Revoke object URL to free memory
            URL.revokeObjectURL(lightboxImage.src);
            setLightboxImage(null);
          }}
        />
      )}
    </div>
  );
}

// ── Main DashboardView ───────────────────────────────────────────────────

export default function DashboardView() {
  const isTabletOrMobile = useIsTabletOrMobile();

  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [, setTick] = useState(0);

  // Auto-refresh relative timestamps every 60s
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // Fetch data: try API first, fall back to static file
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await fetch(buildApiUrl('/api/dashboard', useAppStore.getState().gatewayUrl), {
          headers: { Authorization: `Bearer ${useAppStore.getState().gatewayToken}` },
        });
        if (!res.ok) throw new Error('API not available');
        const json = await res.json();
        if (!json.sprint?.dates) throw new Error('Invalid dashboard payload');
        if (!cancelled) setData(json);
      } catch {
        try {
          const res = await fetch('/dashboard-data.json');
          if (!res.ok) throw new Error('Static fallback not found');
          const json = await res.json();
          if (!json.sprint?.dates) throw new Error('Invalid fallback payload');
          if (!cancelled) setData(json);
        } catch {
          if (!cancelled) setError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const sprintDateRange = useMemo(() => {
    if (!data) return '';
    return `${formatDate(data.sprint.dates.start)} – ${formatDate(data.sprint.dates.end)}`;
  }, [data]);

  const daysLeftUrgency = useMemo(() => {
    if (!data) return 'normal';
    if (data.sprint.daysRemaining <= 1) return 'urgent';
    if (data.sprint.daysRemaining <= 3) return 'warning';
    return 'normal';
  }, [data]);

  // Loading state
  if (loading) {
    return (
      <div className="dashboard-root db-loading">
        <div className="db-loading-inner">
          <div className="db-spinner" />
          <span>Loading dashboard…</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !data) {
    return (
      <div className="dashboard-root db-error-state">
        <div className="db-error-inner">
          <span className="db-error-icon">📊</span>
          <p className="db-error-title">Dashboard data unavailable.</p>
          <p className="db-error-sub">It will be generated shortly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-root">
      {/* ── Sticky header ── */}
      <header className="dashboard-header db-header db-header--compact">
        <div className="db-header-left">
          <div className="db-header-title-row">
            <h1 className="db-header-title">
              <span className="db-header-icon">📊</span>
              Studio Dashboard
            </h1>
            <div className="db-header-badges">
              <span className="db-sprint-badge">Sprint {data.sprint.number}</span>
              <span className={`db-days-badge db-days-badge--${daysLeftUrgency}`}>
                {data.sprint.daysRemaining}d left
              </span>
            </div>
          </div>
          <div className="db-header-meta">
            <span className="db-header-sprint-name">{data.sprint.name}</span>
            <span className="db-header-sep">·</span>
            <span className="db-header-dates">{sprintDateRange}</span>
            <span className="db-header-sep">·</span>
            <span className={`db-header-updated${isStale(data.generatedAt) ? ' db-header-updated--stale' : ''}`}>
              Updated {relativeTime(data.generatedAt)}
            </span>
          </div>
        </div>
      </header>

      {/* ── Content ── */}
      <div className="db-content">

        {/* Blockers (first cell - most important) */}
        <BlockersSection blockers={data.sprint.blockers} />

        {/* Project Cards */}
        {data.projects.length > 0 && (
          <div className="db-projects-grid">
            {data.projects.map((p) => (
              <ProjectCard key={p.id} project={p} />
            ))}
          </div>
        )}

        {/* Sprint Progress + Efficiency (2-col on desktop) */}
        <div className="db-mid-row">
          <SprintProgressSection
            sprint={data.sprint}
            defaultExpanded={!isTabletOrMobile}
          />
          <EfficiencySection efficiency={data.efficiency} />
        </div>

        {/* Highlights + Up Next (2-col on desktop) */}
        <HighlightsSection highlights={data.highlights} nextUp={data.nextUp} />

        {/* Sprint Gates */}
        <GatesSection gates={data.sprint.gates} />

        {/* Project Files Browser */}
        <ProjectFilesSection />

        {/* Bottom padding to keep clear of any future chat widget */}
        <div className="db-bottom-padding" />
      </div>
    </div>
  );
}
